/**
 * Circuit editor for `type: 'Circuit'` events — draw the track loop +
 * place the lap line, all on one Leaflet map.
 *
 * Reuses GateMapPicker's lazy leaflet-loader pattern so the geography
 * deps stay in the lazy chunk.
 *
 * Interaction model:
 *   - "Draw track" mode: every map click appends a vertex to the loop
 *     polyline. Undo pops the last vertex, Clear starts over. The loop
 *     is auto-closed on save (first vertex appended as the last) — the
 *     backend rejects circuit racelines whose ends are >30 m apart.
 *   - "Place lap line" mode: two clicks set endpoints A then B. The
 *     in-progress placement lives in draft state — the saved line is
 *     only replaced once BOTH endpoints are clicked, so exiting the
 *     mode early (toggle off / mode switch / save) keeps the existing
 *     line intact. Lap crossings count when a car passes from the LEFT
 *     of directed A→B to the RIGHT (backend convention) — the map shows
 *     this as a perpendicular arrow at the beam midpoint pointing in the
 *     counted driving direction, and the Flip button swaps endpoints to
 *     reverse it. Deleting the lap line requires the explicit
 *     "Remove lap line" button (pending until save) — it is never
 *     inferred from empty endpoints.
 *   - Save:
 *       track    → GeoJSON LineString built client-side and pushed
 *                  through the EXISTING raceline pipeline
 *                  (validation/RDP/S3 sidecar/public GET all come
 *                  free), then show_raceline is switched on so the
 *                  public viewer renders the loop.
 *       lap line → gates API with kind='lap_line' (POST/PUT diff). The
 *                  backend skips the geofence_revision bump for lap
 *                  lines, so saving mid-event is safe for armed phones.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Eraser, Flag, FlagTriangleRight, Pencil, PlayCircle, Redo2, Save, Trash2, Undo2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  EventGate,
  EventRow,
  addGate,
  deleteGate,
  racelineUrl,
  updateEvent,
  updateGate,
  uploadRaceline,
} from '@/services/fleet';
import RacelineOverlay from './RacelineOverlay';
import BaseLayers from './viewer/BaseLayers';
import { smoothCatmullRom } from '../plugins/synced_viewer/util';

type LeafletModule = typeof import('leaflet');
type ReactLeafletModule = typeof import('react-leaflet');

interface MapModules {
  L: LeafletModule;
  RL: ReactLeafletModule;
}

let mapModulesPromise: Promise<MapModules> | null = null;

async function loadMapModules(): Promise<MapModules> {
  if (!mapModulesPromise) {
    mapModulesPromise = (async () => {
      const [L, RL] = await Promise.all([
        import('leaflet'),
        import('react-leaflet'),
      ]);
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      return { L, RL };
    })();
  }
  return mapModulesPromise;
}

const FALLBACK_CENTER: [number, number] = [58.378, 26.731]; // Tartu, Estonia
const FALLBACK_ZOOM = 7;

type LatLon = [number, number];
type EditMode = 'draw' | 'lap_line' | 'start' | 'finish' | null;

export interface EditorGatePos {
  lat: number | null;
  lon: number | null;
  radius_m: number;
}

interface CircuitMapEditorProps {
  event: EventRow;
  /** Called after a successful save so the parent refreshes gates/raceline metadata. */
  onSaved: () => Promise<void> | void;
  className?: string;
  /** Unified route+gates editing (admin Stages tab): when provided, the
   *  toolbar gains Place start / Place finish modes and the map renders
   *  the gates. Placement updates PARENT form state only — the parent's
   *  own "Save changes" persists gates via its diff-save (this editor's
   *  Save still only handles track + lap line). */
  start?: EditorGatePos;
  finish?: EditorGatePos;
  onStartChange?: (pos: { lat: number; lon: number }) => void;
  onFinishChange?: (pos: { lat: number; lon: number }) => void;
  /** Track drawing + route upload tools (default true). Stage>0 editors
   *  pass false — the route is event-global. */
  routeTools?: boolean;
  /** Lap-line tools (default true). Rally stages have no lap line. */
  lapLineTools?: boolean;
}

function findLapLine(gates: EventGate[]): EventGate | undefined {
  return gates.find((g) => g.kind === 'lap_line' && g.stage_index === 0);
}

const METERS_PER_DEG_LAT = 111_320;

interface DirectionArrow {
  shaft: [LatLon, LatLon];
  head: [[LatLon, LatLon], [LatLon, LatLon]];
}

/**
 * Geometry for the lap-direction arrow: centered on the beam midpoint,
 * perpendicular to the beam, pointing in the driving direction that counts.
 * Backend convention (do not change): a lap counts when the car crosses
 * from the LEFT of directed A→B to its RIGHT, i.e. the A→B vector rotated
 * -90° (clockwise when north is up). Computed in local meters
 * (x = dLon·cos(lat) east, y = dLat north): beam (bx, by) → (by, -bx).
 */
function lapDirectionArrow(a: LatLon, b: LatLon): DirectionArrow | null {
  const midLat = (a[0] + b[0]) / 2;
  const midLon = (a[1] + b[1]) / 2;
  const cosLat = Math.cos((midLat * Math.PI) / 180);
  // Beam vector A→B in local meters (x east, y north).
  const bx = (b[1] - a[1]) * cosLat * METERS_PER_DEG_LAT;
  const by = (b[0] - a[0]) * METERS_PER_DEG_LAT;
  const beamLen = Math.hypot(bx, by);
  if (beamLen < 1e-9) return null; // degenerate A≈B — nothing to point
  // Counted driving direction: A→B rotated -90°, normalized.
  const dirX = by / beamLen;
  const dirY = -bx / beamLen;
  // Shaft ≈40% of the beam length, but never under ~8 m so it stays
  // visible on short beams at track zoom.
  const len = Math.max(beamLen * 0.4, 8);
  const headLen = len * 0.35;
  const toLatLon = (x: number, y: number): LatLon => [
    midLat + y / METERS_PER_DEG_LAT,
    midLon + x / (METERS_PER_DEG_LAT * cosLat),
  ];
  const tipX = dirX * (len / 2);
  const tipY = dirY * (len / 2);
  const tip = toLatLon(tipX, tipY);
  const tail = toLatLon(-tipX, -tipY);
  // Arrowhead barbs: from the tip back along the reverse direction,
  // rotated ±30°.
  const barb = (sign: 1 | -1): LatLon => {
    const sin = sign * Math.sin(Math.PI / 6);
    const cos = Math.cos(Math.PI / 6);
    const vx = -dirX * cos + dirY * sin;
    const vy = -dirX * sin - dirY * cos;
    return toLatLon(tipX + vx * headLen, tipY + vy * headLen);
  };
  return {
    shaft: [tail, tip],
    head: [
      [tip, barb(1)],
      [tip, barb(-1)],
    ],
  };
}

export const CircuitMapEditor: React.FC<CircuitMapEditorProps> = ({
  event,
  onSaved,
  className,
  start,
  finish,
  onStartChange,
  onFinishChange,
  routeTools = true,
  lapLineTools = true,
}) => {
  const [modules, setModules] = useState<MapModules | null>(null);
  const [mode, setMode] = useState<EditMode>(null);
  const [vertices, setVertices] = useState<LatLon[]>([]);
  const [trackDirty, setTrackDirty] = useState(false);
  const [lineA, setLineA] = useState<LatLon | null>(null);
  const [lineB, setLineB] = useState<LatLon | null>(null);
  // First endpoint of an in-progress lap-line placement; lineA/lineB are
  // only overwritten once the second endpoint is clicked.
  const [draftA, setDraftA] = useState<LatLon | null>(null);
  // Explicit "delete the lap line on save" flag — set only by the Remove
  // lap line button, never inferred from lineA/lineB being null.
  const [lineRemoved, setLineRemoved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadMapModules()
      .then((m) => {
        if (!cancelled) setModules(m);
      })
      .catch((err) => console.warn('[circuit-editor] leaflet load failed', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Seed lap line from the event's gates; seed the track from the
  // already-uploaded raceline (admin fetch bypasses show_raceline).
  useEffect(() => {
    const existing = findLapLine(event.gates);
    if (existing && existing.lat2 != null && existing.lon2 != null) {
      setLineA([existing.lat, existing.lon]);
      setLineB([existing.lat2, existing.lon2]);
    } else {
      setLineA(null);
      setLineB(null);
    }
    setDraftA(null);
    setLineRemoved(false);
    setVertices([]);
    setTrackDirty(false);
    if (!event.raceline_uploaded_at) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(racelineUrl(event.id, true));
        if (!res.ok || cancelled) return;
        const geo = await res.json();
        const coords: Array<[number, number]> =
          geo?.type === 'LineString'
            ? geo.coordinates
            : geo?.geometry?.coordinates ?? [];
        if (!cancelled && Array.isArray(coords)) {
          setVertices(coords.map(([lon, lat]) => [lat, lon] as LatLon));
        }
      } catch {
        // Missing raceline just means a blank canvas.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  const handleClickAt = (lat: number, lon: number) => {
    if (mode === 'start') {
      onStartChange?.({ lat, lon });
      setMode(null);
      return;
    }
    if (mode === 'finish') {
      onFinishChange?.({ lat, lon });
      setMode(null);
      return;
    }
    if (mode === 'draw') {
      setVertices((v) => [...v, [lat, lon]]);
      setTrackDirty(true);
    } else if (mode === 'lap_line') {
      if (draftA == null) {
        setDraftA([lat, lon]);
      } else {
        // Second click commits the new line in one go.
        setLineA(draftA);
        setLineB([lat, lon]);
        setDraftA(null);
        setLineRemoved(false);
        setMode(null);
      }
    }
  };

  const undoVertex = () => {
    setVertices((v) => v.slice(0, -1));
    setTrackDirty(true);
  };
  const clearTrack = () => {
    setVertices([]);
    setTrackDirty(true);
  };
  const flipLine = () => {
    if (lineA && lineB) {
      setLineA(lineB);
      setLineB(lineA);
    }
  };

  // Route upload: GeoJSON LineString through the same raceline pipeline
  // as drawn tracks (validation/RDP/S3 sidecar all come free). GPX is a
  // follow-up — the backend parses GeoJSON only.
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const handleRouteFile = async (file: File) => {
    setSaving(true);
    setError(null);
    try {
      await uploadRaceline(event.id, file);
      if (!event.show_raceline) {
        await updateEvent(event.id, { show_raceline: true });
      }
      // Reseed the drawn vertices from the freshly-uploaded route.
      try {
        const res = await fetch(racelineUrl(event.id, true));
        if (res.ok) {
          const geo = await res.json();
          const coords: Array<[number, number]> =
            geo?.type === 'LineString' ? geo.coordinates : geo?.geometry?.coordinates ?? [];
          if (Array.isArray(coords)) {
            setVertices(coords.map(([lon, lat]) => [lat, lon] as LatLon));
            setTrackDirty(false);
          }
        }
      } catch {
        // reseed is cosmetic — the upload already succeeded
      }
      await onSaved();
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'route upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // 1. Track loop → existing raceline pipeline.
      if (trackDirty && vertices.length >= 3) {
        const closed =
          vertices[0][0] === vertices[vertices.length - 1][0] &&
          vertices[0][1] === vertices[vertices.length - 1][1]
            ? vertices
            : [...vertices, vertices[0]];
        const geojson = {
          type: 'LineString',
          coordinates: closed.map(([lat, lon]) => [lon, lat]),
        };
        const file = new File([JSON.stringify(geojson)], 'circuit.geojson', {
          type: 'application/geo+json',
        });
        await uploadRaceline(event.id, file);
        if (!event.show_raceline) {
          await updateEvent(event.id, { show_raceline: true });
        }
        setTrackDirty(false);
      } else if (trackDirty && vertices.length > 0) {
        throw new Error('A circuit needs at least 3 points.');
      }

      // 2. Lap line → gates API (POST/PUT diff; DELETE only on explicit
      //    "Remove lap line" — never just because endpoints are null).
      const existing = findLapLine(event.gates);
      if (lineRemoved) {
        if (existing) {
          await deleteGate(event.id, existing.id);
        }
        setLineA(null);
        setLineB(null);
        setLineRemoved(false);
      } else if (lineA && lineB) {
        const body = {
          lat: lineA[0],
          lon: lineA[1],
          lat2: lineB[0],
          lon2: lineB[1],
        };
        if (!existing) {
          await addGate(event.id, { kind: 'lap_line', radius_m: 50, ...body });
        } else if (
          existing.lat !== body.lat ||
          existing.lon !== body.lon ||
          existing.lat2 !== body.lat2 ||
          existing.lon2 !== body.lon2
        ) {
          await updateGate(event.id, existing.id, body);
        }
      }

      await onSaved();
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const gateCenter: LatLon | null =
    start?.lat != null && start?.lon != null
      ? [start.lat, start.lon]
      : finish?.lat != null && finish?.lon != null
        ? [finish.lat, finish.lon]
        : null;
  const center: LatLon = vertices[0] ?? lineA ?? gateCenter ?? FALLBACK_CENTER;
  const zoom = vertices.length > 0 || lineA || gateCenter ? 14 : FALLBACK_ZOOM;

  // While placing, the map shows the draft endpoint; otherwise it shows the
  // committed line (hidden when a removal is pending).
  const displayA = mode === 'lap_line' ? draftA : lineRemoved ? null : lineA;
  const displayB = mode === 'lap_line' ? null : lineRemoved ? null : lineB;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {routeTools && (
          <>
            <Button
              type="button"
              variant={mode === 'draw' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setDraftA(null);
                setMode(mode === 'draw' ? null : 'draw');
              }}
              className="gap-1"
            >
              <Pencil className="size-4 text-accent" />
              {mode === 'draw' ? 'Click map to add points' : 'Draw track'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={undoVertex}
              disabled={vertices.length === 0}
              className="gap-1"
            >
              <Undo2 className="size-4" />
              Undo
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearTrack}
              disabled={vertices.length === 0}
              className="gap-1"
            >
              <Eraser className="size-4" />
              Clear
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={saving}
              className="gap-1"
              title="Upload a GeoJSON LineString route (GPX support is a follow-up)"
            >
              <Upload className="size-4" />
              Upload route
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleRouteFile(f);
                e.target.value = '';
              }}
            />
            <span className="mx-1 h-4 w-px bg-border" />
          </>
        )}
        {(onStartChange || onFinishChange) && (
          <>
            {onStartChange && (
              <Button
                type="button"
                variant={mode === 'start' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setDraftA(null);
                  setMode(mode === 'start' ? null : 'start');
                }}
                className="gap-1"
              >
                <PlayCircle className="size-4 text-status-online" />
                {mode === 'start' ? 'Click map — start' : 'Place start'}
              </Button>
            )}
            {onFinishChange && (
              <Button
                type="button"
                variant={mode === 'finish' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setDraftA(null);
                  setMode(mode === 'finish' ? null : 'finish');
                }}
                className="gap-1"
              >
                <FlagTriangleRight className="size-4 text-status-error" />
                {mode === 'finish' ? 'Click map — finish' : 'Place finish'}
              </Button>
            )}
            <span className="mx-1 h-4 w-px bg-border" />
          </>
        )}
        {lapLineTools && (
          <>
        <Button
          type="button"
          variant={mode === 'lap_line' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => {
            // Drafts are throwaway — entering or exiting never touches the
            // committed lineA/lineB.
            setDraftA(null);
            setMode(mode === 'lap_line' ? null : 'lap_line');
          }}
          className="gap-1"
        >
          <Flag className="size-4 text-status-error" />
          {mode === 'lap_line'
            ? draftA
              ? 'Click endpoint B'
              : 'Click endpoint A'
            : 'Place lap line'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={flipLine}
          disabled={!lineA || !lineB || lineRemoved}
          className="gap-1"
          title="Flip if the arrow points the wrong way"
        >
          <Redo2 className="size-4" />
          Flip direction
        </Button>
        {lineA && lineB && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setLineRemoved((r) => !r)}
            className="gap-1"
            title={
              lineRemoved
                ? 'Removal pending — click to keep the lap line'
                : 'Delete the lap line on next save'
            }
          >
            <Trash2 className="size-4 text-status-error" />
            {lineRemoved ? 'Undo remove' : 'Remove lap line'}
          </Button>
        )}
        {lineRemoved && (
          <span className="text-xs text-status-error">
            Lap line will be deleted on save.
          </span>
        )}
          </>
        )}
        <div className="flex-1" />
        {(routeTools || lapLineTools) && (
          <Button type="button" size="sm" onClick={handleSave} disabled={saving} className="gap-1">
            <Save className="size-4" />
            {saving ? 'Saving…' : lapLineTools ? 'Save circuit' : 'Save route'}
          </Button>
        )}
        {savedFlash && <span className="text-xs text-status-online">Saved.</span>}
      </div>

      {error && (
        <div className="rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-xs text-status-error">
          {error}
        </div>
      )}

      <div className="h-[420px] overflow-hidden rounded border border-border bg-bg-video">
        {!modules ? (
          <div className="flex h-full items-center justify-center text-xs text-fg-secondary">
            Loading map…
          </div>
        ) : (
          <EditorMap
            modules={modules}
            center={center}
            zoom={zoom}
            vertices={vertices}
            lineA={displayA}
            lineB={displayB}
            start={start ?? null}
            finish={finish ?? null}
            mode={mode}
            onClickAt={handleClickAt}
          />
        )}
      </div>
      <p className="text-xs text-fg-secondary">
        {lapLineTools
          ? 'The arrow on the lap line shows the driving direction that counts — use Flip if it points the wrong way. Timing is GPS-based and indicative (±0.5 s), not official. '
          : ''}
        {(onStartChange || onFinishChange) &&
          'Gate placements update the panes below — the page-level Save changes persists them.'}
      </p>
    </div>
  );
};

const EditorMap: React.FC<{
  modules: MapModules;
  center: LatLon;
  zoom: number;
  vertices: LatLon[];
  lineA: LatLon | null;
  lineB: LatLon | null;
  start: EditorGatePos | null;
  finish: EditorGatePos | null;
  mode: EditMode;
  onClickAt: (lat: number, lon: number) => void;
}> = ({ modules, center, zoom, vertices, lineA, lineB, start, finish, mode, onClickAt }) => {
  const { MapContainer, Circle, CircleMarker, Polyline, Tooltip, useMapEvents } =
    modules.RL;

  // Direction arrow derives from the same displayed endpoints as the beam
  // Polyline, so it follows draft/removed visibility and flips live.
  const arrow = lineA && lineB ? lapDirectionArrow(lineA, lineB) : null;

  // WYSIWYG ribbon — the SAME Catmull-Rom-smoothed cased track the
  // public viewer renders, recomputed live from the editing vertices
  // (cheap: ~200 points in <1 ms). Mounted first so it sits UNDER the
  // purple vertex polyline + handles; opacity dialed down so the
  // basemap detail and the editing affordances stay readable through it
  // (satellite tarmac in particular makes the real track legible). The
  // ring only rounds its seam once the loop is actually closed (ends
  // within 30 m) — same rule the save-time validation enforces.
  const ribbon = useMemo<Array<[number, number]>>(
    () => (vertices.length > 2 ? smoothCatmullRom(vertices) : []),
    [vertices],
  );

  const ClickHandler: React.FC = () => {
    useMapEvents({
      click(e: any) {
        if (mode == null) return;
        onClickAt(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  };

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      scrollWheelZoom
      style={{ height: '100%', width: '100%', cursor: mode ? 'crosshair' : 'grab' }}
    >
      <BaseLayers modules={modules} />
      <ClickHandler />
      {ribbon.length > 1 && (
        <RacelineOverlay
          modules={modules}
          coords={ribbon}
          casingOpacity={0.4}
          surfaceOpacity={0.5}
        />
      )}
      {vertices.length > 1 && (
        <Polyline
          positions={vertices}
          pathOptions={{ color: '#a085ff', weight: 3, opacity: 0.9 }}
        />
      )}
      {vertices.map((v, i) => (
        <CircleMarker
          key={`v-${i}`}
          center={v}
          radius={i === 0 ? 6 : 3}
          pathOptions={{
            color: '#a085ff',
            fillColor: i === 0 ? '#ffffff' : '#a085ff',
            fillOpacity: 0.9,
            weight: 2,
          }}
        >
          {i === 0 && (
            <Tooltip direction="top" offset={[0, -6]}>
              Track start/end
            </Tooltip>
          )}
        </CircleMarker>
      ))}
      {start && start.lat != null && start.lon != null && (
        <>
          <Circle
            center={[start.lat, start.lon]}
            radius={start.radius_m}
            pathOptions={{ color: '#34d399', fillColor: '#34d399', fillOpacity: 0.12, weight: 1.5, dashArray: '4 4' }}
          />
          <CircleMarker
            center={[start.lat, start.lon]}
            radius={7}
            pathOptions={{ color: '#34d399', fillColor: '#34d399', fillOpacity: 0.9, weight: 2 }}
          >
            <Tooltip permanent direction="top" offset={[0, -8]}>
              <span style={{ color: '#0d0d12' }}>Start</span>
            </Tooltip>
          </CircleMarker>
        </>
      )}
      {finish && finish.lat != null && finish.lon != null && (
        <>
          <Circle
            center={[finish.lat, finish.lon]}
            radius={finish.radius_m}
            pathOptions={{ color: '#f87171', fillColor: '#f87171', fillOpacity: 0.12, weight: 1.5, dashArray: '4 4' }}
          />
          <CircleMarker
            center={[finish.lat, finish.lon]}
            radius={7}
            pathOptions={{ color: '#f87171', fillColor: '#ffffff', fillOpacity: 0.9, weight: 2.5 }}
          >
            <Tooltip permanent direction="top" offset={[0, -8]}>
              <span style={{ color: '#0d0d12' }}>Finish</span>
            </Tooltip>
          </CircleMarker>
        </>
      )}
      {lineA && lineB && (
        <Polyline
          positions={[lineA, lineB]}
          pathOptions={{ color: '#f87171', weight: 4 }}
        />
      )}
      {arrow && (
        <>
          <Polyline positions={arrow.shaft} pathOptions={{ color: '#f87171', weight: 3 }}>
            <Tooltip permanent direction="top" offset={[0, -6]}>
              <span style={{ color: '#0d0d12' }}>laps count this way</span>
            </Tooltip>
          </Polyline>
          <Polyline positions={arrow.head[0]} pathOptions={{ color: '#f87171', weight: 3 }} />
          <Polyline positions={arrow.head[1]} pathOptions={{ color: '#f87171', weight: 3 }} />
        </>
      )}
      {lineA && (
        <CircleMarker
          center={lineA}
          radius={8}
          pathOptions={{ color: '#f87171', fillColor: '#f87171', fillOpacity: 0.95, weight: 2 }}
        >
          <Tooltip permanent direction="top" offset={[0, -8]}>
            <strong style={{ color: '#0d0d12' }}>A</strong>
          </Tooltip>
        </CircleMarker>
      )}
      {lineB && (
        <CircleMarker
          center={lineB}
          radius={8}
          pathOptions={{ color: '#f87171', fillColor: '#ffffff', fillOpacity: 0.95, weight: 3 }}
        >
          <Tooltip permanent direction="top" offset={[0, -8]}>
            <strong style={{ color: '#0d0d12' }}>B</strong>
          </Tooltip>
        </CircleMarker>
      )}
    </MapContainer>
  );
};

export default CircuitMapEditor;
